import { find, isEmpty } from "lodash/fp";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useZapier from "@app/analytics/hooks/integrations/useZapier";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import IntegrationUpsell from "@app/analytics/Integrations/components/IntegrationUpsell";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import {
  IntegrationDetailContent,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import Config from "@app/config";

import Button from "@rui/Button";
import Link from "@rui/Link";
import { Row } from "@rui/Stack";

import ApiKey from "./ApiKey";
import { ZAP_TEMPLATES } from "./constants";
import ZapTemplate from "./ZapTemplate";

const CONNECT_CTA = {
  label: "Configure in Zapier",
  show: true,
  href: Config.integrations.zapier.CONFIGURE_URL,
};

const ZapierIntegrationConfig = () => {
  const { currentPlan, plansInitialLoading } = useBillingContext();

  const { userIntegrationsByCategory, isLoading: isIntegrationsLoading } =
    useIntegrations();

  const integration = find(
    { providerId: "zapier" },
    userIntegrationsByCategory.workflow
  );

  const {
    apiKeys,
    createApiKey,
    isLoading: isZapierLoading,
  } = useZapier({ enabled: !(integration?.isDisabled ?? true) });

  if (!plansInitialLoading && !isPaidPlan(currentPlan.type)) {
    return <IntegrationUpsell type="premium" />;
  }

  const isLoading =
    plansInitialLoading || isIntegrationsLoading || isZapierLoading;

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleCreateApiKey = () => {
    createApiKey.mutate(undefined, {
      onError: () => {
        toast.error(
          "Something went wrong and an API key could not be generated. Please try again or reach out to support@read.ai if the problem persists"
        );
      },
    });
  };

  const hasApiKeys = !isEmpty(apiKeys);
  const providerContent = PROVIDER_CONTENT[integration.providerId];

  return (
    <>
      <IntegrationDetailHeader
        cta={CONNECT_CTA}
        provider={integration}
        documentationUrl={integration.documentationUrl}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isDisabled ? (
        <IntegrationNotConnected cta={CONNECT_CTA} provider={integration} />
      ) : (
        <IntegrationDetailContent spacing={1.5}>
          <Typography variant="bodySecondaryMed" color="shades.neutral.800">
            Configuration
          </Typography>
          {hasApiKeys ? (
            <>
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                Copy the API key below into {integration.displayName} to start
                creating Zaps with Read.
                <ol>
                  <li>
                    Navigate to the{" "}
                    <Link
                      underline
                      fontWeight={400}
                      href={LINKS.THIRD_PARTY.READ_ZAPIER_APP}
                    >
                      Read AI Zapier app
                    </Link>
                  </li>
                  <li>Click "+ Add connection"</li>
                  <li>Paste your API key to connect</li>
                </ol>
              </Typography>
              {apiKeys.map((apiKey) => (
                <ApiKey key={apiKey.id} apiKey={apiKey} />
              ))}
            </>
          ) : (
            <>
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                Generate an API key to get started
              </Typography>
              <Row>
                <Button
                  onClick={handleCreateApiKey}
                  loading={createApiKey.isPending}
                  variant="outlined"
                  sx={{ bgcolor: "#FFF" }}
                >
                  Generate API key
                </Button>
              </Row>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="bodySecondaryMed" color="shades.neutral.800">
            More information
          </Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Try a popular {integration.displayName} template using Read
          </Typography>
          {ZAP_TEMPLATES.map((template) => (
            <ZapTemplate key={template.app} {...template} />
          ))}
        </IntegrationDetailContent>
      )}
    </>
  );
};

export default ZapierIntegrationConfig;
